import React, { useEffect } from 'react';

import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import NearMeIcon from '@mui/icons-material/NearMe';
import {
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Box,
    Button,
    Grid,
    IconButton,
    Typography,
} from '@sunwisesoftware/sunwise-ui';
import get from 'lodash/get';
import isEqual from 'lodash/isEqual';
import isNil from 'lodash/isNil';
import PropTypes from 'prop-types';
import { useGeolocated } from 'react-geolocated';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';

import { ControlledMap, ShowErrors } from 'common/components';
import {
    ReactHookFormDragnDropFile,
    ReactHookFormInput,
    ReactHookFormIntlNumberInput,
} from 'common/components/form/bootstrap';
import {
    ItemGeocodeResult,
    ListGeocodeResult,
    WrapperGeocodeResult,
} from 'common/components/maps';
import {
    DEFAULT_MAP_PREVIEW_ZOOM,
    FILE_ALLOWED_EXTENSIONS,
    MAX_SMALL_FILE_SIZE,
} from 'common/constants';
import yupResolver from 'common/utils/yupResolver';

import * as actions from '../actions';
import * as selectors from '../selectors';
import validate from '../validate';

import MapWarning from './MapWarning';

const accordionProps = {
    defaultExpanded: true,
    disableGutters: true,
    sx: {
        boxShadow: 'none!important',
        '&::before': { display: 'none' },
    },
};

const accordionSummaryProps = {
    expandIcon: <ExpandMoreIcon />,
    sx: {
        borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
        flexDirection: 'row-reverse',
        mb: 1,
        p: 0,
        '& .MuiAccordionSummary-expandIconWrapper.Mui-expanded': {
            transform: 'rotate(90deg)',
        },
        '& .MuiAccordionSummary-content': {
            ml: 1,
        },
    },
};

const FormContainer = ({
    accountCountryPositionLatLng,
    errors,
    fetchCompanyData,
    fetchGeocodePoint,
    geocodePointData,
    geocodeResult,
    handleClickSave,
    handleGeocode,
    defaultValues,
    isFetchingGeocodePoint,
    isSaving,
    setEmptyGeocodeResult,
    setShowGeocodeResults,
    showGeocodeResults,
}) => {
    const { t } = useTranslation();
    const { coords, getPosition, isGeolocationEnabled } = useGeolocated({
        positionOptions: {
            enableHighAccuracy: false,
        },
        userDecisionTimeout: 5000,
        suppressLocationOnMount: true,
    });

    const { control, handleSubmit, reset, setValue, watch } = useForm({
        defaultValues,
        resolver: yupResolver(validate),
    });
    const formValues = watch();

    const position = get(formValues, 'position', {});

    const disabledButton = isEqual(defaultValues, formValues);

    const isVisibleMapWarning = () => {
        if (!isNil(formValues.position) && isNil(formValues.latitude))
            return true;

        if (!isNil(formValues.position) && isNil(formValues.longitude))
            return true;

        return false;
    };

    const updateLocation = (position) => {
        if (position) {
            setValue('position', {
                latitude: position.latitude,
                longitude: position.longitude,
            });
            setValue('latitude', position.latitude);
            setValue('longitude', position.longitude);
        }
    };

    useEffect(() => {
        fetchCompanyData();
    }, []);

    useEffect(() => {
        if (coords) updateLocation(coords);
    }, [coords]);

    useEffect(() => {
        if (!isNil(geocodePointData)) setValue('description', geocodePointData);
    }, [geocodePointData]);

    useEffect(() => reset(defaultValues), [defaultValues]);

    useEffect(() => {
        if (position) {
            setValue('latitude', position.latitude);
            setValue('longitude', position.longitude);
        }
    }, [position]);

    useEffect(() => {
        if (
            formValues.latitude !== defaultValues.latitude ||
            formValues.longitude !== defaultValues.longitude
        )
            fetchGeocodePoint(formValues.latitude, formValues.longitude);
    }, [formValues.latitude, formValues.longitude]);

    return (
        <Box component="form">
            <Grid container>
                <Grid
                    size={{ lg: 9, md: 18, xs: 18 }}
                    sx={{ maxHeight: '40em' }}
                >
                    <Accordion {...accordionProps}>
                        <AccordionSummary {...accordionSummaryProps}>
                            <Typography fontWeight="bold" variant="caption">
                                {t('Company image')}
                            </Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                            <Grid container justifyContent="center">
                                <Grid size={{ md: 8, xs: 18 }}>
                                    <ReactHookFormDragnDropFile
                                        accept={FILE_ALLOWED_EXTENSIONS.IMAGE}
                                        avatarData={{
                                            image: formValues?.image,
                                            name: formValues?.name,
                                        }}
                                        control={control}
                                        formats="jpg, jpeg, png"
                                        id="company_image"
                                        maxSize={MAX_SMALL_FILE_SIZE}
                                        name="image"
                                        setValue={setValue}
                                        variant="profile"
                                    />
                                </Grid>
                            </Grid>
                        </AccordionDetails>
                    </Accordion>

                    <Accordion {...accordionProps}>
                        <AccordionSummary {...accordionSummaryProps}>
                            <Typography fontWeight="bold" variant="caption">
                                {t('Location')}
                            </Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                            <Grid container>
                                <Grid size="grow">
                                    {geocodeResult &&
                                        geocodeResult.length > 0 &&
                                        showGeocodeResults && (
                                            <WrapperGeocodeResult>
                                                <ListGeocodeResult
                                                    style={{
                                                        transform:
                                                            'translate3d(0px, 38px, 0px)',
                                                    }}
                                                >
                                                    {geocodeResult.map(
                                                        (result, i) => (
                                                            <ItemGeocodeResult
                                                                key={i}
                                                                onClick={() => {
                                                                    setValue(
                                                                        'description',
                                                                        result.formatted_address,
                                                                    );

                                                                    const {
                                                                        lat,
                                                                        lng,
                                                                    } =
                                                                        result
                                                                            .geometry
                                                                            .location;
                                                                    updateLocation(
                                                                        {
                                                                            latitude:
                                                                                lat,
                                                                            longitude:
                                                                                lng,
                                                                        },
                                                                    );
                                                                }}
                                                            >
                                                                {
                                                                    result.formatted_address
                                                                }
                                                            </ItemGeocodeResult>
                                                        ),
                                                    )}
                                                </ListGeocodeResult>
                                            </WrapperGeocodeResult>
                                        )}
                                    <ReactHookFormInput
                                        control={control}
                                        disabled={isFetchingGeocodePoint}
                                        name="description"
                                        onChange={({ target }) => {
                                            if (target.value.length >= 6)
                                                handleGeocode(target.value);
                                            else setEmptyGeocodeResult();
                                            setValue(
                                                'description',
                                                target.value,
                                            );
                                        }}
                                        onBlur={() => {
                                            setTimeout(() => {
                                                setShowGeocodeResults(false);
                                            }, 150);
                                        }}
                                        onFocus={() =>
                                            setShowGeocodeResults(true)
                                        }
                                        placeholder={t('Location')}
                                    />
                                </Grid>
                            </Grid>

                            <Grid container>
                                <Grid size="grow">
                                    <ReactHookFormIntlNumberInput
                                        control={control}
                                        fullWidth
                                        name="latitude"
                                        placeholder={t('Latitude')}
                                        id="business_general_latitude"
                                    />
                                </Grid>

                                <Grid size="grow">
                                    <ReactHookFormIntlNumberInput
                                        control={control}
                                        fullWidth
                                        name="longitude"
                                        placeholder={t('Longitude')}
                                        id="business_general_longitude"
                                    />
                                </Grid>

                                <Grid size="auto">
                                    <IconButton
                                        disabled={
                                            !isGeolocationEnabled || isSaving
                                        }
                                        onClick={() => {
                                            if (!isNil(coords))
                                                updateLocation({
                                                    latitude: coords.latitude,
                                                    longitude: coords.longitude,
                                                });
                                            else getPosition();
                                        }}
                                        sx={{
                                            backgroundColor: '#1f3c53',
                                            color: '#fff',
                                            '&:hover, &.Mui-focusVisible': {
                                                backgroundColor:
                                                    'rgba(31, 60, 83, 0.9)',
                                            },
                                        }}
                                    >
                                        <NearMeIcon fontSize="small" />
                                    </IconButton>
                                </Grid>
                            </Grid>

                            <Grid container>
                                <Grid size="grow">
                                    <Controller
                                        control={control}
                                        name="position"
                                        render={({
                                            field: { onChange, value },
                                        }) => (
                                            <ControlledMap
                                                center={
                                                    accountCountryPositionLatLng
                                                }
                                                heightMap="206px"
                                                onChange={onChange}
                                                title={t('Location')}
                                                value={value}
                                                zoom={DEFAULT_MAP_PREVIEW_ZOOM}
                                                id="business_general_address"
                                            />
                                        )}
                                    />
                                </Grid>
                            </Grid>
                            <MapWarning visible={isVisibleMapWarning()} />
                        </AccordionDetails>
                    </Accordion>
                </Grid>
                <Grid size={{ lg: 9, md: 18, xs: 18 }}>
                    <Accordion {...accordionProps}>
                        <AccordionSummary {...accordionSummaryProps}>
                            <Typography fontWeight="bold" variant="caption">
                                {t('Company profile')}
                            </Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                            <ReactHookFormInput
                                control={control}
                                label={t('Tradename')}
                                name="name"
                                placeholder={t('Tradename')}
                                id="business_general_form_text_tradename"
                            />
                            <ReactHookFormInput
                                control={control}
                                label={t('Business activity')}
                                name="business_activity"
                                placeholder={t('Business activity')}
                                id="business_general_form_text_activity"
                            />
                            <ReactHookFormInput
                                control={control}
                                label={t('Business name')}
                                name="business_name"
                                placeholder={t('Business name')}
                                id="business_general_form_text_business_name"
                            />
                            <ReactHookFormInput
                                control={control}
                                label={t('Tax ID')}
                                name="rfc"
                                placeholder={t('Tax ID')}
                                id="business_general_form_string_tax_id"
                            />
                        </AccordionDetails>
                    </Accordion>

                    <Accordion {...accordionProps}>
                        <AccordionSummary {...accordionSummaryProps}>
                            <Typography fontWeight="bold" variant="caption">
                                {t('Contact information')}
                            </Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                            <ReactHookFormInput
                                control={control}
                                label={t('Web page')}
                                name="web_page"
                                placeholder={t('Web page')}
                                id="business_general_form_text_website"
                            />
                            <ReactHookFormInput
                                control={control}
                                label={t('Email contact company')}
                                name="contact_email"
                                placeholder={t('Email contact company')}
                                id="business_general_form_text_email"
                            />
                            <ReactHookFormInput
                                control={control}
                                label={t('Phone number company')}
                                name="contact_telephone"
                                placeholder={t('Phone number company')}
                                id="business_general_form_number_phone"
                            />
                            <ReactHookFormInput
                                control={control}
                                label={t('WhatsApp company')}
                                name="contact_cellphone"
                                placeholder={t('WhatsApp company')}
                                id="business_general_form_number_whatsapp"
                            />
                        </AccordionDetails>
                    </Accordion>
                </Grid>
            </Grid>

            <Grid container sx={{ mt: { lg: 0, xs: 2 } }}>
                <Grid size="grow" sx={{ textAlign: 'right' }}>
                    <Button
                        disabled={disabledButton}
                        id="business_general_form_save_button"
                        onClick={handleSubmit(handleClickSave)}
                        endIcon={<ArrowForwardIcon />}
                        sx={{ mt: 2, width: { md: 'auto', xs: '100%' } }}
                        variant="outlined"
                    >
                        {isSaving ? t('Saving').concat('...') : t('Save')}
                    </Button>
                </Grid>
            </Grid>
            <ShowErrors errors={errors} />
        </Box>
    );
};

const mapStateToProps = createStructuredSelector({
    accountCountryPositionLatLng: selectors.getAccountCountryPositionLatLng,
    defaultValues: selectors.getInitialValues,
    errors: selectors.getErrorsSave,
    geocodePointData: selectors.getGeocodePointData,
    geocodeResult: selectors.getFetchGeocodeData,
    isFetchingGeocodePoint: selectors.getIsFetchingGeocodePoint,
    isNewRecord: selectors.getIsNewRecord,
    isSaving: selectors.getIsSaving,
    showGeocodeResults: selectors.showGeocodeResults,
});

const mapDispatchToProps = (dispatch) => ({
    fetchCompanyData: () => dispatch(actions.fetchCompanyData()),
    fetchGeocodePoint: (lat, lng) =>
        dispatch(actions.fetchGeocodePoint(lat, lng)),
    handleClickSave: (values) => dispatch(actions.save(values)),
    handleGeocode: (str) => dispatch(actions.fetchGeocodeResult(str)),
    setEmptyGeocodeResult: () => dispatch(actions.setEmptyGeocodeResult()),
    setShowGeocodeResults: (value) =>
        dispatch(actions.setShowGeocodeResults(value)),
});

FormContainer.propTypes = {
    accountCountryPositionLatLng: PropTypes.object,
    defaultValues: PropTypes.object,
    errors: PropTypes.array,
    fetchCompanyData: PropTypes.func,
    fetchGeocodePoint: PropTypes.func,
    geocodePointData: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
    geocodeResult: PropTypes.array,
    handleClickSave: PropTypes.func,
    handleGeocode: PropTypes.func,
    isFetchingGeocodePoint: PropTypes.bool,
    isSaving: PropTypes.bool,
    setEmptyGeocodeResult: PropTypes.func,
    setShowGeocodeResults: PropTypes.func,
    showGeocodeResults: PropTypes.bool,
};

export default connect(mapStateToProps, mapDispatchToProps)(FormContainer);
